/**
 * 画布状态 store（P1 C-1，zustand）——与导演台 store 同一套纪律。
 *
 * 职责边界：这里是画布文档的唯一可变出口。所有增删改都过这里并维持不变量：
 *  · 节点 id 唯一；
 *  · 连线两端必须指向存在的节点，禁自环、禁重复边；
 *  · 删节点级联删掉它牵连的所有边（否则 validateCanvasDoc 判悬空、保存即报错）。
 * 组件一律不得直接改 doc，避免"某处改了、校验器没跑过"的漂移。
 */
import { create } from 'zustand';

import { createEmptyCanvas, validateCanvasDoc, type CanvasDoc, type CanvasEdge, type CanvasNode, type Vec2 } from '../../model/canvas';
import { getActiveId, loadCanvas, migrateLegacy, newCanvasId, saveCanvas, setActiveId } from './canvasLibrary';
import { COALESCE_MS, pushPast, redoStep, undoStep } from '../director/history';

function emptyDoc(): CanvasDoc {
  return createEmptyCanvas(newCanvasId(), new Date().toISOString());
}

/** 初始文档：先把早期单画布快照迁入库，再优先回到上次打开的那张；无则空画布 */
function loadInitialDoc(): CanvasDoc {
  migrateLegacy();
  const id = getActiveId();
  if (id !== null) {
    const doc = loadCanvas(id);
    if (doc !== null) {
      return doc;
    }
  }
  return emptyDoc();
}

/** 每次变更落盘：写该画布全文 + 索引 + 活动指针（多画布各自存，刷新/切画布不串） */
function persistDoc(doc: CanvasDoc): void {
  saveCanvas(doc);
  setActiveId(doc.id);
}

/** 生成不冲突的 n 前缀 id（纯函数，可测） */
export function nextNodeId(existing: readonly string[]): string {
  let i = 1;
  while (existing.includes(`n${i}`)) {
    i += 1;
  }
  return `n${i}`;
}

export function nextEdgeId(existing: readonly string[]): string {
  let i = 1;
  while (existing.includes(`e${i}`)) {
    i += 1;
  }
  return `e${i}`;
}

export interface CanvasStore {
  doc: CanvasDoc;

  /** 撤销/重做历史（doc 快照栈）；_lastKey/_lastAt 用于拖拽等连续变更合并 */
  _past: CanvasDoc[];
  _future: CanvasDoc[];
  _lastKey: string | null;
  _lastAt: number;

  setDoc(doc: CanvasDoc): boolean;
  resetDoc(): void;
  setTitle(title: string): void;

  addNode(node: CanvasNode): boolean;
  /** 复制节点：新 id、位置阶梯偏移；浅拷贝其余字段（media/params/payload） */
  duplicateNode(id: string): boolean;
  updateNode(id: string, patch: Partial<Omit<CanvasNode, 'id'>>): boolean;
  moveNode(id: string, position: Vec2): boolean;
  removeNode(id: string): boolean;

  /** 建连线：拒绝自环、重复边、端点不存在 */
  connect(from: string, to: string): boolean;
  removeEdge(id: string): boolean;

  /** 原子地从源节点派生多个视图节点并各连一条边（一次事务 = 一步撤销） */
  spawnViews(sourceId: string, newNodes: CanvasNode[]): boolean;

  undo(): void;
  redo(): void;

  /** 跨页桥接：待导演台落成关键帧的目标节点 id（瞬时态，不入 doc、不持久化） */
  directorTarget: string | null;
  setDirectorTarget(id: string | null): void;
}

function nowIso(): string {
  return new Date().toISOString();
}

export const useCanvasStore = create<CanvasStore>()((set, get) => {
  /** 所有 doc 变更的唯一出口：写新 doc + 记历史（同目标短时窗合并为一步） */
  function applyDoc(next: CanvasDoc, key: string | null): void {
    const st = get();
    const now = Date.now();
    const coalesce = key !== null && key === st._lastKey && now - st._lastAt < COALESCE_MS;
    set({
      doc: next,
      _past: pushPast(st._past, st.doc, coalesce),
      _future: [],
      _lastKey: key,
      _lastAt: now,
    });
  }

  return {
    doc: loadInitialDoc(),
    _past: [],
    _future: [],
    _lastKey: null,
    _lastAt: 0,
    directorTarget: null,

    setDoc(doc) {
      if (validateCanvasDoc(doc).length > 0) {
        return false;
      }
      // 载入/切画布是历史边界：清空 past/future
      set({ doc, _past: [], _future: [], _lastKey: null });
      return true;
    },

    resetDoc() {
      set({ doc: createEmptyCanvas(get().doc.id, nowIso()), _past: [], _future: [], _lastKey: null });
    },

    setTitle(title) {
      const { doc } = get();
      applyDoc({ ...doc, title, updatedAt: nowIso() }, 'title');
    },

    addNode(node) {
      const { doc } = get();
      if (doc.nodes.some((n) => n.id === node.id)) {
        return false;
      }
      applyDoc({ ...doc, nodes: [...doc.nodes, node], updatedAt: nowIso() }, 'add:' + node.id);
      return true;
    },

    duplicateNode(id) {
      const { doc } = get();
      const src = doc.nodes.find((n) => n.id === id);
      if (src === undefined) {
        return false;
      }
      const newId = nextNodeId(doc.nodes.map((n) => n.id));
      const copy: CanvasNode = {
        ...src,
        id: newId,
        position: { x: src.position.x + 28, y: src.position.y + 28 },
      };
      applyDoc({ ...doc, nodes: [...doc.nodes, copy], updatedAt: nowIso() }, 'add:' + newId);
      return true;
    },

    updateNode(id, patch) {
      const { doc } = get();
      if (!doc.nodes.some((n) => n.id === id)) {
        return false;
      }
      const nodes = doc.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n));
      applyDoc({ ...doc, nodes, updatedAt: nowIso() }, 'upd:' + id);
      return true;
    },

    moveNode(id, position) {
      return get().updateNode(id, { position });
    },

    removeNode(id) {
      const { doc } = get();
      if (!doc.nodes.some((n) => n.id === id)) {
        return false;
      }
      applyDoc(
        {
          ...doc,
          nodes: doc.nodes.filter((n) => n.id !== id),
          edges: doc.edges.filter((e) => e.from !== id && e.to !== id),
          updatedAt: nowIso(),
        },
        'del:' + id,
      );
      return true;
    },

    connect(from, to) {
      const { doc } = get();
      if (from === to) {
        return false; // 禁自环
      }
      if (!doc.nodes.some((n) => n.id === from) || !doc.nodes.some((n) => n.id === to)) {
        return false; // 端点必须存在
      }
      if (doc.edges.some((e) => e.from === from && e.to === to)) {
        return false; // 禁重复边
      }
      const edge: CanvasEdge = { id: nextEdgeId(doc.edges.map((e) => e.id)), from, to };
      applyDoc({ ...doc, edges: [...doc.edges, edge], updatedAt: nowIso() }, `connect:${from}->${to}`);
      return true;
    },

    removeEdge(id) {
      const { doc } = get();
      if (!doc.edges.some((e) => e.id === id)) {
        return false;
      }
      applyDoc({ ...doc, edges: doc.edges.filter((e) => e.id !== id), updatedAt: nowIso() }, 'deledge:' + id);
      return true;
    },

    spawnViews(sourceId, newNodes) {
      const { doc } = get();
      if (!doc.nodes.some((n) => n.id === sourceId)) {
        return false;
      }
      const nodes = [...doc.nodes];
      const edges = [...doc.edges];
      for (const vn of newNodes) {
        if (nodes.some((n) => n.id === vn.id)) {
          continue;
        }
        nodes.push(vn);
        edges.push({ id: nextEdgeId(edges.map((e) => e.id)), from: sourceId, to: vn.id });
      }
      applyDoc({ ...doc, nodes, edges, updatedAt: nowIso() }, 'spawn:' + sourceId);
      return true;
    },

    undo() {
      const st = get();
      const step = undoStep(st._past, st.doc, st._future);
      if (step === null) {
        return;
      }
      set({ doc: step.present, _past: step.past, _future: step.future, _lastKey: null });
    },

    redo() {
      const st = get();
      const step = redoStep(st._past, st.doc, st._future);
      if (step === null) {
        return;
      }
      set({ doc: step.present, _past: step.past, _future: step.future, _lastKey: null });
    },

    setDirectorTarget(id) {
      set({ directorTarget: id });
    },
  };
});

// 任何 doc 变化都落盘（当前画布）；订阅只在后续 set 触发，不会在初始化时多写一次。
useCanvasStore.subscribe((state) => persistDoc(state.doc));
